import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { cache } from '../utils/localCache';

const Ctx = createContext();

export const AppProvider = ({ children }) => {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings,   setSettings]   = useState({ store_name:'MINIMARKET', store_address:'Arequipa, Perú', ruc:'', cashier:'' });
  const [cart,       setCart]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [online,     setOnline]     = useState(navigator.onLine);
  const [syncing,    setSyncing]    = useState(false);
  const [page,       setPage]       = useState('main');
  const [notification, setNotif]   = useState(null);
  const notifTimer = useRef(null);

  /* ── Online detection ── */
  useEffect(() => {
    const on  = () => { setOnline(true);  flushAndSync(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  /* ── Notify ── */
  const notify = useCallback((msg, type = 'ok') => {
    if (notifTimer.current) clearTimeout(notifTimer.current);
    setNotif({ msg, type, id: Date.now() });
    notifTimer.current = setTimeout(() => setNotif(null), 2400);
  }, []);

  /* ── Load data ── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      if (navigator.onLine) {
        const [{ products: rp }, { categories: rc }, { settings: rs }] = await Promise.all([
          api.getProducts(), api.getCategories(), api.getSettings(),
        ]);
        setProducts(rp);  await cache.setProducts(rp);
        setCategories(rc); await cache.setCategories(rc);
        setSettings(rs);
      } else {
        setProducts(await cache.getAllProducts());
        setCategories(await cache.getAllCategories());
      }
    } catch {
      setProducts(await cache.getAllProducts());
      setCategories(await cache.getAllCategories());
    } finally {
      setLoading(false);
    }
  }, []);

  const flushAndSync = useCallback(async () => {
    if (!navigator.onLine || syncing) return;
    setSyncing(true);
    try { await api.flushQueue(); await loadAll(); }
    finally { setSyncing(false); }
  }, [syncing, loadAll]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── Products CRUD ── */
  const createProduct = useCallback(async (data) => {
    try {
      const { product } = await api.createProduct(data);
      setProducts(p => [product, ...p]);
      await cache.upsertProduct(product);
      notify('Producto agregado ✓');
      return product;
    } catch (e) { notify(e.message, 'err'); return null; }
  }, [notify]);

  const updateProduct = useCallback(async (id, data) => {
    try {
      const { product } = await api.updateProduct(id, data);
      setProducts(p => p.map(x => x.id === id ? product : x));
      await cache.upsertProduct(product);
      notify('Producto actualizado ✓');
      return product;
    } catch (e) { notify(e.message, 'err'); return null; }
  }, [notify]);

  const deleteProduct = useCallback(async (id) => {
    try {
      await api.deleteProduct(id);
      setProducts(p => p.filter(x => x.id !== id));
      await cache.removeProduct(id);
      notify('Producto eliminado', 'warn');
      return true;
    } catch (e) { notify(e.message, 'err'); return false; }
  }, [notify]);

  /* ── Categories CRUD ── */
  const createCategory = useCallback(async (data) => {
    try {
      const { category } = await api.createCategory(data);
      setCategories(c => [...c, category]);
      notify('Categoría agregada ✓');
      return category;
    } catch (e) { notify(e.message, 'err'); return null; }
  }, [notify]);

  const updateCategory = useCallback(async (id, data) => {
    try {
      const { category } = await api.updateCategory(id, data);
      setCategories(c => c.map(x => x.id === id ? category : x));
      notify('Categoría actualizada ✓');
      return category;
    } catch (e) { notify(e.message, 'err'); return null; }
  }, [notify]);

  const deleteCategory = useCallback(async (id) => {
    try {
      await api.deleteCategory(id);
      setCategories(c => c.filter(x => x.id !== id));
      setProducts(p => p.map(x => x.category_id === id ? { ...x, category_id: 'otro' } : x));
      notify('Categoría eliminada', 'warn');
      return true;
    } catch (e) { notify(e.message, 'err'); return false; }
  }, [notify]);

  const saveSettings = useCallback(async (data) => {
    try {
      await api.saveSettings(data);
      setSettings(data);
      notify('Configuración guardada ✓');
    } catch (e) { notify(e.message, 'err'); }
  }, [notify]);

  /* ── Cart ── */
  const addToCart = useCallback((item) => {
    setCart(prev => {
      // Productos por peso siempre se agregan como entrada separada (cada pesada es única)
      if (item.by_weight) {
        return [...prev, { ...item, qty: item.qty || 1 }];
      }
      const idx = prev.findIndex(i => i.id === item.id && !item.manual);
      if (idx >= 0) return prev.map((i, n) => n === idx ? { ...i, qty: i.qty + (item.qty || 1) } : i);
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    notify(item.name + ' agregado ✓');
  }, [notify]);

  const updateCartQty = useCallback((idx, delta) => {
    setCart(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], qty: next[idx].qty + delta };
      if (next[idx].qty <= 0) next.splice(idx, 1);
      return next;
    });
  }, []);

  // Actualiza campos específicos de un ítem del carrito (usado para editar peso)
  const updateCartItem = useCallback((idx, changes) => {
    setCart(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...changes };
      return next;
    });
  }, []);

  const removeFromCart = useCallback((idx) => {
    setCart(prev => { const n = [...prev]; n.splice(idx,1); return n; });
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  return (
    <Ctx.Provider value={{
      products, categories, settings, cart, loading, online, syncing,
      notification, page, setPage,
      cartCount: cart.length,
      loadAll, flushAndSync, notify,
      createProduct, updateProduct, deleteProduct,
      createCategory, updateCategory, deleteCategory,
      saveSettings,
      addToCart, updateCartQty, updateCartItem, removeFromCart, clearCart,
    }}>
      {children}
    </Ctx.Provider>
  );
};

export const useApp = () => useContext(Ctx);
